/*
 *	Service to interact with the backend API for fetching and updating modules and their categories.
 *	Provides functions to get module categories, get modules, and toggle module states.
 */

// Base URL of the API
// const API_URL = 'http://localhost:8000/api';
import { getApiBaseUrl } from 'common/services/apiBaseUrl';
const apiBaseUrl = getApiBaseUrl();
export const API_URL = apiBaseUrl;

import { apiFetch } from 'common/services/apiFetch';

// Import necessary types and icons
import { faPuzzlePiece } from '@fortawesome/free-solid-svg-icons';
import { iconMap } from '../data/iconMap';
import { Module, ModuleCategory } from '../types/types';

// Interface representing a module as received from the API
export interface ModuleData {
	id: number;
	name: string;
	title: string;
	description: string;
	enabled: boolean;
	required: boolean;
	configurable: boolean;
	icon: string;
	category: string;
}

// Interface representing a module category as received from the API
export interface ModuleCategoryData {
	id: number;
	name: string;
	title: string;
}

// Modules ayant une page de paramétrage réelle sous /app/settings/:name
const SETTINGS_MODULE_PATHS = new Set([
	'workflows',
	'infrastructure',
	'produits',
	'services',
	'tva',
	'depenses',
	'documents',
	'import',
]);

// Fetch all categories
export const getModuleCategories = async (
	token: string
): Promise<ModuleCategoryData[]> => {
	const res = await apiFetch(`${API_URL}/module_categories`, {
		headers: {
			Authorization: `Bearer ${token}`,
			'Content-Type': 'application/json',
		},
	});

	// Error handling for failed requests
	if (!res.ok) {
		throw new Error('Erreur lors de la récupération des catégories de modules');
	}

	// Parse the response data
	const data = await res.json();
	return data.member || [];
};

// Fetch all modules
export const getModules = async (token: string): Promise<ModuleData[]> => {
	// Fetch the list of modules from the API
	const res = await apiFetch(`${API_URL}/modules`, {
		headers: {
			Authorization: `Bearer ${token}`,
			'Content-Type': 'application/json',
		},
	});

	// Error handling for failed requests
	if (!res.ok) {
		throw new Error('Erreur lors de la récupération des modules');
	}

	// Parse the response data
	const data = await res.json();
	return data.member || [];
};

// Toggle module (enable or disable)
export const toggleModule = async (
	token: string,
	name: string,
	enabled: boolean
): Promise<ModuleData> => {
	// Fetch the list of modules to find the ID by its name
	const modules = await getModules(token);
	const module = modules.find((m) => m.name === name);

	// If the module is not found, throw an error
	if (!module) {
		throw new Error(`Module "${name}" introuvable`);
	}

	// Send a PATCH request to update the module's enabled state
	const res = await apiFetch(`${API_URL}/modules/${module.id}`, {
		method: 'PATCH',
		headers: {
			Authorization: `Bearer ${token}`,
			'Content-Type': 'application/merge-patch+json',
		},
		body: JSON.stringify({ enabled }),
	});

	// Error handling for failed requests
	if (!res.ok) {
		throw new Error('Erreur lors de la mise à jour du module');
	}

	// Return the updated module data
	return res.json();
};

// Ordre d'affichage des catégories (nom technique en DB)
const CATEGORY_ORDER: string[] = ['GRH', 'GRC', 'GRF', 'GPS', 'GPO', 'SYS'];

// Ordre d'affichage des modules au sein de chaque catégorie (nom technique en DB).
// Les modules absents apparaissent en dernier.
const MODULE_ORDER: string[] = [
	// GRH
	'users',
	'badge',
	// GRC
	'clients',
	'contacts',
	'devis_client',
	'commandes_client',
	'factures_client',
	// GRF
	'fournisseurs',
	'commandes_fournisseurs',
	'contrats',
	'factures_fournisseurs',
	// GPS
	'produits',
	'services',
	'tva',
	// GPO
	'affaires',
	'activites',
	'depenses',
	'resultats',
	// SYS
	'workflows',
	'infrastructure',
	'documents',
	'import',
];

// Fetch modules and categories, transforming data to match Module and ModuleCategory interfaces
export async function getModulesAndCategories(token: string): Promise<{
	modules: Module[];
	modulesCategories: ModuleCategory[];
}> {
	try {
		// Fetch modules and categories from API
		const modulesData: ModuleData[] = await getModules(token);
		const modulesCategoriesData: ModuleCategoryData[] =
			await getModuleCategories(token);

		// Transform data to match Module and ModuleCategory interfaces
		const transformedCategories = modulesCategoriesData.map((c) => ({
			id: c.id.toString(),
			name: c.name,
			title: c.title ?? c.name,
		}));

		// Create a map for category ID to name
		const categoryMap = Object.fromEntries(
			transformedCategories.map((c) => [c.id.toString(), c.name])
		);

		// Trie les catégories dans l'ordre métier défini
		transformedCategories.sort(
			(a, b) => CATEGORY_ORDER.indexOf(a.name) - CATEGORY_ORDER.indexOf(b.name)
		);

		// Map modules data to include category names and icons
		const modules = modulesData.map((m) => {
			const match = m.category.match(/\/(\d+)$/);
			const categoryId = match ? match[1] : null;
			return {
				id: m.id.toString(),
				name: m.name,
				enabled: m.enabled,
				required: m.required,
				configurable:
					Boolean(m.configurable) && SETTINGS_MODULE_PATHS.has(m.name),
				title: m.title ?? m.name,
				description: m.description ?? '',
				category: categoryId ? categoryMap[categoryId] : 'SYS',
				icon: iconMap[m.icon] || faPuzzlePiece,
			};
		});

		// Trie les modules par ordre de catégorie puis par ordre interne dans la catégorie
		modules.sort((a, b) => {
			const catDiff =
				CATEGORY_ORDER.indexOf(a.category) - CATEGORY_ORDER.indexOf(b.category);
			if (catDiff !== 0) return catDiff;
			return MODULE_ORDER.indexOf(a.name) - MODULE_ORDER.indexOf(b.name);
		});

		return { modules, modulesCategories: transformedCategories };
	} catch (error) {
		// Handle errors during fetch
		console.error(error);
		return { modules: [], modulesCategories: [] };
	}
}
